// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyClientDetailResponse } from "@/types";
import Skeleton from "@/UI/atoms/Skeleton";
import ActionsBar from "@/UI/components/ActionsBar";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function OneClientScreen() {
    const t = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { canEdit } = usePermission();

    const { data, loading, error, invoke } = useFetch<TyApiResponse<TyClientDetailResponse>>(`clients/${id}`);
    const save = useFetch(`clients/${id}`, { method: "PUT" }, false);

    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState("");
    const [gender, setGender] = useState<"male" | "female" | "">("");

    const client = data?.data.client;

    useEffect(() => {
        if (!client) return;
        setName(client.name);
        setGender(client.gender ?? "");
    }, [client]);

    useEffect(() => {
        if (!save.data) return;
        setIsEditing(false);
        invoke();
    }, [save.data]);

    if (loading) {
        return (
            <div className="one-client-screen">
                <Skeleton className="one-client-skeleton-head" />
                <Skeleton className="one-client-skeleton-section" />
            </div>
        );
    }

    if (error || !client) {
        return (
            <div className="one-client-state">
                <p className="one-client-state-title">{error ?? t.clients.notFound}</p>
                <button type="button" className="one-client-retry" onClick={() => invoke()}>
                    {t.stores.retry}
                </button>
            </div>
        );
    }

    const { owned, access, supportTickets } = data!.data;

    const genderLabel = gender === "male" ? t.clients.genderMale : gender === "female" ? t.clients.genderFemale : t.clients.genderUnset;

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        save.invoke({ data: { name, gender: gender === "" ? null : gender } });
    };

    return (
        <div className="one-client-screen">
            <ActionsBar title={client.name} onBack={() => navigate("/clients")} />

            <section className="one-client-head">
                <div className="one-client-avatar">
                    {client.picture ? (
                        <img src={client.picture} alt={client.name} />
                    ) : (
                        <img className="one-client-avatar-fallback" src="/icons/client-small-placeholder-icon.svg" alt="" />
                    )}
                </div>

                {!isEditing ? (
                    <>
                        <div className="one-client-head-main">
                            <h1 className="one-client-name">{client.name}</h1>
                            <p className="one-client-phone" dir="ltr">
                                {client.phone}
                            </p>
                            <p className="one-client-meta">
                                {genderLabel} · {t.clients.joined}{" "}
                                {new Date(client.createdAt).toLocaleDateString(lang === "en" ? "en-GB" : "ar-EG", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                })}
                            </p>
                        </div>
                        {canEdit("clients") && (
                            <button type="button" className="one-client-edit" onClick={() => setIsEditing(true)}>
                                {t.clients.edit}
                            </button>
                        )}
                    </>
                ) : (
                    <form className="one-client-form" onSubmit={handleSave}>
                        <label className="one-client-field">
                            <span>{t.clients.formName}</span>
                            <input value={name} onChange={(e) => setName(e.target.value)} />
                        </label>
                        <label className="one-client-field">
                            <span>{t.clients.formGender}</span>
                            <select value={gender} onChange={(e) => setGender(e.target.value as "male" | "female" | "")}>
                                <option value="">{t.clients.genderUnset}</option>
                                <option value="male">{t.clients.genderMale}</option>
                                <option value="female">{t.clients.genderFemale}</option>
                            </select>
                        </label>
                        {save.error && <p className="one-client-error">{save.error}</p>}
                        <div className="one-client-form-actions">
                            <button type="button" className="one-client-cancel" onClick={() => setIsEditing(false)}>
                                {t.clients.formCancel}
                            </button>
                            <button type="submit" className="one-client-save" disabled={save.loading}>
                                {t.clients.formSave}
                            </button>
                        </div>
                    </form>
                )}
            </section>

            <section className="one-client-section">
                <p className="one-client-section-title">
                    {t.clients.ownedStores} <span className="one-client-count">{owned.length}</span>
                </p>
                {owned.length === 0 ? (
                    <p className="one-client-note">{t.clients.noStores}</p>
                ) : (
                    <div className="one-client-stores">
                        {owned.map((store) => (
                            <Link key={store._id} to={`/stores/${store._id}`} className="one-client-store">
                                <p className="one-client-store-name">{store.name || store.slug}</p>
                                <p className="one-client-store-slug" dir="ltr">
                                    {store.slug}
                                </p>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            <section className="one-client-section">
                <p className="one-client-section-title">
                    {t.clients.accessStores} <span className="one-client-count">{access.length}</span>
                </p>
                {access.length === 0 ? (
                    <p className="one-client-note">{t.clients.noStores}</p>
                ) : (
                    <div className="one-client-stores">
                        {access.map((entry) => (
                            <Link key={entry.store._id} to={`/stores/${entry.store._id}`} className="one-client-store">
                                <p className="one-client-store-name">{entry.store.name || entry.store.slug}</p>
                                <p className="one-client-store-slug" dir="ltr">
                                    {entry.store.slug}
                                </p>
                                <p className="one-client-store-perms">
                                    {entry.permissions.map((p) => p.model).join(" · ") || "—"}
                                    {entry.branches === "all" ? ` · ${t.clients.allBranches}` : ""}
                                </p>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            <section className="one-client-section">
                <p className="one-client-section-title">{t.clients.supportTickets}</p>
                <p className="one-client-tickets">{supportTickets}</p>
            </section>
        </div>
    );
}
